import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { VariantProps, cva } from "class-variance-authority"
import { cn } from "../../lib/utils"

export interface SidebarProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode
}

export interface SidebarBrandingProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Custom logo image URL */
  logoUrl?: string
  /** Company/app name */
  companyName?: string
  /** Company/app subtitle/description */
  companySubtitle?: string
  /** Custom icon component (React component) */
  icon?: React.ComponentType<any>
  /** Fallback initials for the icon */
  initials?: string
  /** Size variant */
  size?: "default" | "sm" | "lg"
}

const SidebarBranding = React.forwardRef<HTMLDivElement, SidebarBrandingProps>(
  ({ 
    logoUrl, 
    companyName = "Agentela", 
    companySubtitle = "Engineering Platform", 
    icon: Icon, 
    initials,
    size = "default",
    className, 
    ...props 
  }, ref) => {
    const sizeClasses = {
      sm: { container: "h-7 w-7", text: "text-sm", subtitle: "text-xs" },
      default: { container: "h-9 w-9", text: "text-lg", subtitle: "text-xs" },
      lg: { container: "h-11 w-11", text: "text-xl", subtitle: "text-sm" }
    }

    const currentSize = sizeClasses[size]
    const fallbackInitials = initials || companyName.charAt(0).toUpperCase()

    return (
      <div
        ref={ref}
        className={cn("flex items-center space-x-3", className)}
        {...props}
      >
        {/* Logo/Icon */}
        <div className={cn(
          "rounded-lg bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center flex-shrink-0",
          currentSize.container
        )}>
          {logoUrl ? (
            <img 
              src={logoUrl} 
              alt={`${companyName} logo`}
              className={cn("rounded-lg object-cover", currentSize.container)}
            />
          ) : Icon ? (
            <Icon className={cn("text-primary-foreground", size === "sm" ? "h-3 w-3" : size === "lg" ? "h-6 w-6" : "h-4 w-4")} />
          ) : (
            <span className={cn("text-primary-foreground font-bold", size === "sm" ? "text-xs" : size === "lg" ? "text-lg" : "text-sm")}>
              {fallbackInitials}
            </span>
          )}
        </div>
        
        {/* Company Info */}
        <div className="flex-1 min-w-0">
          <h2 className={cn("font-semibold text-sidebar-foreground truncate", currentSize.text)}>
            {companyName}
          </h2>
          {companySubtitle && (
            <p className={cn("text-sidebar-muted-foreground truncate", currentSize.subtitle)}>
              {companySubtitle}
            </p>
          )}
        </div>
      </div>
    )
  }
)
SidebarBranding.displayName = "SidebarBranding"

const Sidebar = React.forwardRef<HTMLDivElement, SidebarProps>(
  ({ children, className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          "flex h-full w-64 flex-col bg-sidebar-background text-sidebar-foreground border-r border-sidebar-border",
          className
        )}
        {...props}
      >
        {children}
      </div>
    )
  }
)
Sidebar.displayName = "Sidebar"

const SidebarHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ children, className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "flex items-center px-6 py-6 border-b border-sidebar-border bg-sidebar-background",
      className
    )}
    {...props}
  >
    {children}
  </div>
))
SidebarHeader.displayName = "SidebarHeader"

const SidebarContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ children, className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex-1 overflow-auto px-4 py-4", className)}
    {...props}
  >
    {children}
  </div>
))
SidebarContent.displayName = "SidebarContent"

const SidebarFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ children, className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "border-t border-sidebar-border p-4 bg-sidebar-background",
      className
    )}
    {...props}
  >
    {children}
  </div>
))
SidebarFooter.displayName = "SidebarFooter"

// Enhanced navigation components
const SidebarGroup = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ children, className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("py-2", className)}
    {...props}
  >
    {children}
  </div>
))
SidebarGroup.displayName = "SidebarGroup"

const SidebarGroupLabel = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ children, className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "flex items-center px-3 mb-2 text-xs font-semibold uppercase tracking-wider text-left text-gradient-gold-purple",
      className
    )}
    {...props}
  >
    {children}
  </div>
))
SidebarGroupLabel.displayName = "SidebarGroupLabel"

const SidebarMenu = React.forwardRef<
  HTMLUListElement,
  React.HTMLAttributes<HTMLUListElement>
>(({ children, className, ...props }, ref) => (
  <ul
    ref={ref}
    className={cn("space-y-1", className)}
    {...props}
  >
    {children}
  </ul>
))
SidebarMenu.displayName = "SidebarMenu"

const SidebarMenuItem = React.forwardRef<
  HTMLLIElement,
  React.HTMLAttributes<HTMLLIElement>
>(({ children, className, ...props }, ref) => (
  <li
    ref={ref}
    className={cn("", className)}
    {...props}
  >
    {children}
  </li>
))
SidebarMenuItem.displayName = "SidebarMenuItem"

interface SidebarMenuButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  asChild?: boolean
  isActive?: boolean
  size?: "default" | "sm" | "lg"
}

const sidebarMenuButtonVariants = cva(
  "flex w-full items-center gap-2 rounded-md text-left text-sm font-medium transition-colors overflow-hidden outline-none ring-sidebar-ring focus-visible:ring-2 disabled:pointer-events-none disabled:opacity-50 [&>span:last-child]:truncate [&>svg]:size-4 [&>svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground active:bg-sidebar-accent active:text-sidebar-accent-foreground",
        outline: "bg-background shadow-[0_0_0_1px_hsl(var(--sidebar-border))] hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
      },
      size: {
        default: "h-9 px-3 py-2",
        sm: "h-8 px-2 py-1.5 text-sm",
        lg: "h-10 px-4 py-2.5"
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

const SidebarMenuButton = React.forwardRef<
  HTMLButtonElement,
  SidebarMenuButtonProps & VariantProps<typeof sidebarMenuButtonVariants>
>(({ children, className, isActive, size = "default", asChild, ...props }, ref) => {
  const Comp = asChild ? Slot : "button"

  return (
    <Comp
      ref={ref}
      data-active={isActive}
      className={cn(
        sidebarMenuButtonVariants({ size }),
        isActive 
          ? "bg-sidebar-accent text-sidebar-accent-foreground shadow-sm font-medium" 
          : "text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
        className
      )}
      {...props}
    >
      {children}
    </Comp>
  )
})
SidebarMenuButton.displayName = "SidebarMenuButton"

const SidebarMenuSub = React.forwardRef<
  HTMLUListElement,
  React.HTMLAttributes<HTMLUListElement>
>(({ children, className, ...props }, ref) => (
  <ul
    ref={ref}
    className={cn("mt-1 ml-4 space-y-1 border-l border-sidebar-border pl-4", className)}
    {...props}
  >
    {children}
  </ul>
))
SidebarMenuSub.displayName = "SidebarMenuSub"

const SidebarMenuSubItem = React.forwardRef<
  HTMLLIElement,
  React.HTMLAttributes<HTMLLIElement>
>(({ children, className, ...props }, ref) => (
  <li
    ref={ref}
    className={cn("", className)}
    {...props}
  >
    {children}
  </li>
))
SidebarMenuSubItem.displayName = "SidebarMenuSubItem"

const SidebarMenuSubButton = React.forwardRef<
  HTMLButtonElement,
  SidebarMenuButtonProps & VariantProps<typeof sidebarMenuButtonVariants>
>(({ children, className, isActive, size = "sm", asChild, ...props }, ref) => {
  const Comp = asChild ? Slot : "button"

  return (
    <Comp
      ref={ref}
      data-active={isActive}
      className={cn(
        "flex w-full items-center gap-2 rounded-md text-left text-sm transition-colors overflow-hidden outline-none ring-sidebar-ring focus-visible:ring-1 disabled:pointer-events-none disabled:opacity-50",
        size === "sm" && "h-7 px-2 py-1 text-xs",
        size === "default" && "h-8 px-2 py-1.5 text-sm",
        size === "lg" && "h-9 px-3 py-2 text-sm",
        isActive 
          ? "bg-sidebar-accent text-sidebar-accent-foreground font-medium" 
          : "text-sidebar-muted-foreground hover:text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
        className
      )}
      {...props}
    >
      {children}
    </Comp>
  )
})
SidebarMenuSubButton.displayName = "SidebarMenuSubButton"

const SidebarMenuBadge = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "absolute right-1 flex h-5 min-w-5 items-center justify-center rounded-md px-1 text-xs font-medium tabular-nums text-sidebar-foreground select-none pointer-events-none",
      "peer-hover/menu-button:text-sidebar-accent-foreground peer-data-[active=true]/menu-button:text-sidebar-accent-foreground",
      className
    )}
    {...props}
  />
))
SidebarMenuBadge.displayName = "SidebarMenuBadge"

export { 
  Sidebar, 
  SidebarBranding,
  SidebarHeader, 
  SidebarContent, 
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarMenuBadge,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton
}
